import { Component, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { AuthService } from '../_services/auth.service';
import { BrandingService } from '../_services/branding.service';
import { FirstLoginService, FirstLoginState } from './first-login.service';
import { FirstLoginStep } from './first-login.models';
import { PasswordPattern } from '../_interfaces/password-pattern';

import { ProgressIndicatorComponent } from './widgets/progress-indicator.component';
import { EulaStepComponent } from './steps/eula-step.component';
import { PasswordStepComponent } from './steps/password-step.component';
import { PhoneStepComponent } from './steps/phone-step.component';
import { PhoneConfirmationStepComponent } from './steps/phone-confirmation-step.component';
import { EmailStepComponent } from './steps/email-step.component';
import { EmailConfirmationStepComponent } from './steps/email-confirmation-step.component';
import { EmailSuccessStepComponent } from './steps/email-success-step.component';
import { SpecialtyStepComponent } from './steps/specialty-step.component';
import { TemplatesStepComponent } from './steps/templates-step.component';
import { DoneStepComponent } from './steps/done-step.component';

@Component({
  selector: 'app-first-login',
  standalone: true,
  imports: [
    CommonModule, MatIconModule, MatButtonModule, TranslateModule,
    ProgressIndicatorComponent,
    EulaStepComponent, PasswordStepComponent,
    PhoneStepComponent, PhoneConfirmationStepComponent,
    EmailStepComponent, EmailConfirmationStepComponent, EmailSuccessStepComponent,
    SpecialtyStepComponent, TemplatesStepComponent, DoneStepComponent,
  ],
  host: { class: 'block h-full' },
  templateUrl: './first-login.component.html',
})
export class FirstLoginComponent implements OnInit, OnDestroy {
  @ViewChild('phoneConfirmStep') phoneConfirmStep?: PhoneConfirmationStepComponent;
  @ViewChild('emailConfirmStep') emailConfirmStep?: EmailConfirmationStepComponent;

  Step = FirstLoginStep;
  currentStep = FirstLoginStep.License;
  progressSteps: FirstLoginStep[] = [];
  progressIndex = 0;

  isLoading = false;
  errorMessage: string | null = null;
  showLogoutDialog = false;

  // Password step
  passwordPattern: PasswordPattern = {};
  currentPasswordError: string | null = null;

  // Verification
  phoneVerificationError = false;
  emailVerificationError = false;
  resendDisabled = false;
  cooldownSeconds = 0;

  isMobile = false;

  private subs: Subscription[] = [];

  constructor(
    public ftlService: FirstLoginService,
    private authService: AuthService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    public branding: BrandingService,
  ) {}

  @HostListener('window:popstate')
  onBrowserBack(): void {
    // Push state back so the user stays on the page
    history.pushState(null, '', location.href);
    this.showLogoutDialog = true;
  }

  get showStepTitle(): boolean {
    return this.currentStep !== FirstLoginStep.ContactEmailSuccess &&
      this.currentStep !== FirstLoginStep.Done;
  }

  get stepTitle(): string {
    switch (this.currentStep) {
      case FirstLoginStep.License: return 'EULA';
      case FirstLoginStep.Password: return 'Reset password';
      case FirstLoginStep.PhoneNumber: return 'Add a mobile number';
      case FirstLoginStep.PhoneConfirmation: return 'Confirm your mobile number';
      case FirstLoginStep.ContactEmail: return 'Add a contact email';
      case FirstLoginStep.ContactEmailConfirmation: return 'Confirm your email';
      case FirstLoginStep.Speciality: return 'Select your speciality';
      case FirstLoginStep.Templates: return 'All blueprints';
      default: return '';
    }
  }

  ngOnInit(): void {
    // Push a history entry so browser back triggers popstate instead of navigating away
    history.pushState(null, '', location.href);

    this.subs.push(
      this.breakpointObserver.observe('(max-width: 767px)').subscribe(state => {
        this.isMobile = state.matches;
      }),
    );

    this.ftlService.initialize().then(() => {
      this.progressSteps = this.ftlService.getProgressSteps();
    });

    // Build password pattern from entity settings
    const user = this.authService.getUser();
    if (user?.entity?.settings) {
      const es = user.entity.settings;
      this.passwordPattern = {
        passwordLength: es.passwordLength || 6,
        passwordLowercase: es.passwordLowercase,
        passwordUppercase: es.passwordUppercase,
        passwordNumbers: es.passwordNumbers,
        passwordSymbols: es.passwordSymbols,
      };
    }

    this.subs.push(
      this.ftlService.currentStep$.subscribe(step => {
        this.currentStep = step;
        this.progressIndex = this.ftlService.getProgressIndex();
        this.progressSteps = this.ftlService.getProgressSteps();
        // Reset verification errors on step change
        this.phoneVerificationError = false;
        this.emailVerificationError = false;
        this.currentPasswordError = null;
      }),
    );

    this.subs.push(
      this.ftlService.state$.subscribe(state => {
        this.isLoading = state.type === 'loading';
        this.resendDisabled = state.type === 'resendCooldown';
        this.cooldownSeconds = state.type === 'resendCooldown' ? state.secondsRemaining : 0;

        if (state.type === 'error') {
          this.handleError(state);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  // --- Step Handlers ---

  onAcceptLicense(): void {
    this.ftlService.acceptLicense();
  }

  onChangePassword(event: { currentPassword: string; newPassword: string }): void {
    this.currentPasswordError = null;
    this.ftlService.savePassword(event.currentPassword, event.newPassword);
  }

  onSubmitPhone(event: { phoneNumber: string; countryCode: string }): void {
    this.ftlService.sendPhoneVerification(event.phoneNumber, event.countryCode);
  }

  async onVerifyPhoneCode(code: string): Promise<void> {
    this.phoneVerificationError = false;
    const success = await this.ftlService.verifyPhoneCode(code);
    if (!success) {
      this.phoneVerificationError = true;
      // OTP component handles clear + shake + re-enable internally via hasError input
    }
  }

  onResendPhoneCode(): void {
    this.ftlService.resendPhoneCode();
  }

  onSkipPhone(): void {
    this.ftlService.skipPhone();
  }

  onSubmitEmail(email: string): void {
    this.ftlService.sendEmailVerification(email);
  }

  async onVerifyEmailCode(code: string): Promise<void> {
    this.emailVerificationError = false;
    const success = await this.ftlService.verifyEmailCode(code);
    if (!success) {
      this.emailVerificationError = true;
      // OTP component handles clear + shake + re-enable internally via hasError input
    }
  }

  onResendEmailCode(): void {
    this.ftlService.resendEmailCode();
  }

  onSkipEmail(): void {
    this.ftlService.skipEmail();
  }

  onSubmitSpeciality(speciality: string): void {
    this.ftlService.saveSpeciality(speciality);
  }

  onSubmitTemplates(templates: { id: number; type: string }[]): void {
    this.ftlService.saveTemplates(templates);
  }

  onComplete(): void {
    this.router.navigate(['/home']);
  }

  // --- Navigation ---

  onBackClick(): void {
    if (this.ftlService.canGoBack()) {
      this.ftlService.previousStep();
    } else {
      this.showLogoutDialog = true;
    }
  }

  onLogout(): void {
    this.showLogoutDialog = false;
    this.authService.logout();
  }

  // --- Error handling ---

  private handleError(state: FirstLoginState & { type: 'error' }): void {
    const msg = state.message;

    // Inline errors for specific steps
    if (this.currentStep === FirstLoginStep.Password) {
      this.currentPasswordError = msg;
      this.ftlService.resetState();
      return;
    }
    if (this.currentStep === FirstLoginStep.PhoneConfirmation && msg.includes('Incorrect')) {
      // Already handled via phoneVerificationError
      this.ftlService.resetState();
      return;
    }
    if (this.currentStep === FirstLoginStep.ContactEmailConfirmation && msg.includes('Incorrect')) {
      // Already handled via emailVerificationError
      this.ftlService.resetState();
      return;
    }

    // Global error dialog for other errors
    this.errorMessage = msg;
  }
}
